import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatNaira, formatDateDDMMYYYY, formatDateTimeWAT } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { X, User, Building2, Shield, Mail, Phone, MapPin } from "lucide-react";

const ProfileCtx = createContext(null);

export const ProfileProvider = ({ children }) => {
  const [open, setOpen] = useState(null); // { kind: 'guard'|'client'|'user', id }
  const value = useMemo(() => ({ open, setOpen }), [open]);

  return (
    <ProfileCtx.Provider value={value}>
      {children}
      {open && <ProfileDrawer payload={open} onClose={() => setOpen(null)} />}
    </ProfileCtx.Provider>
  );
};

export const useProfile = () => useContext(ProfileCtx);

// ProfileLink — wrap a name to make it clickable from anywhere
export const ProfileLink = ({ kind, id, children, className = "" }) => {
  const ctx = useProfile();
  if (!ctx || !id) return <span className={className}>{children}</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); ctx.setOpen({ kind, id }); }}
      className={`text-left underline-offset-4 hover:underline ${className}`}
      style={{ color: "var(--brand-teal)", background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
      data-testid={`profile-link-${kind}-${id}`}
    >
      {children}
    </button>
  );
};

const ProfileDrawer = ({ payload, onClose }) => {
  const { kind, id } = payload;
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState({});

  useEffect(() => {
    setData(null);
    setExtras({});
    if (kind === "guard") {
      api.get(`/guards/${id}`).then(r => setData(r.data));
      api.get(`/shifts?guard_id=${id}`).then(r => setExtras(e => ({ ...e, shifts: r.data })));
      api.get(`/reports?guard_id=${id}`).then(r => setExtras(e => ({ ...e, reports: r.data })));
      api.get(`/tours?guard_id=${id}`).then(r => setExtras(e => ({ ...e, tours: r.data })));
    } else if (kind === "client") {
      api.get(`/clients/${id}`).then(r => setData(r.data));
      api.get(`/sites?client_id=${id}`).then(r => setExtras(e => ({ ...e, sites: r.data })));
      api.get(`/contracts?client_id=${id}`).then(r => setExtras(e => ({ ...e, contracts: r.data })));
      api.get(`/invoices`).then(r => setExtras(e => ({ ...e, invoices: r.data.filter(i => i.client_id === id) })));
    } else if (kind === "user") {
      api.get(`/users/${id}`).then(r => setData(r.data));
    }
  }, [kind, id]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
               display: "flex", justifyContent: "flex-end" }}
      data-testid="profile-drawer-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxWidth: "92vw", background: "var(--shield-navy)",
                 borderLeft: "1px solid var(--border-dark)", overflowY: "auto",
                 padding: 24, color: "#fff" }}
        data-testid="profile-drawer"
      >
        <div className="flex justify-between items-center mb-4">
          <div className="text-muted" style={{ fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            {kind} profile
          </div>
          <button onClick={onClose} className="text-muted" data-testid="close-drawer"><X size={20} /></button>
        </div>

        {!data && <div className="text-muted">Loading…</div>}

        {data && kind === "guard" && <GuardProfileView data={data} extras={extras} />}
        {data && kind === "client" && <ClientProfileView data={data} extras={extras} />}
        {data && kind === "user" && <UserProfileView data={data} />}
      </div>
    </div>
  );
};

const GuardProfileView = ({ data, extras }) => (
  <>
    <div className="flex items-center gap-4 mb-5">
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(46,202,200,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "#2ECAC8", fontSize: 26, fontWeight: 500 }}>
        {data.full_name?.[0]}
      </div>
      <div>
        <div className="h-page" style={{ fontSize: 20 }}>{data.full_name}</div>
        <div className="text-muted" style={{ fontSize: 13 }}>{data.rank} · {data.department}</div>
      </div>
    </div>
    <Field icon={Phone} label="Phone" value={data.phone} />
    <Field icon={Shield} label="NIN" value={data.nin} mono />
    <Field icon={MapPin} label="Sites assigned" value={(data.assigned_site_ids || []).length} />
    <Section title="Skills">
      <div className="flex flex-wrap gap-2">
        {(data.skills || []).map(s => <span key={s} className="badge badge-neutral">{s}</span>)}
        {(data.skills || []).length === 0 && <span className="text-muted" style={{ fontSize: 13 }}>None recorded</span>}
      </div>
    </Section>
    <Section title="Performance">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Score" value={data.performance?.score || 0} />
        <Stat label="Attendance" value={`${data.performance?.attendance_pct || 0}%`} />
        <Stat label="Tours done" value={data.performance?.tours_completed || 0} />
        <Stat label="Reports" value={data.performance?.reports_submitted || 0} />
      </div>
    </Section>
    <Section title={`Recent shifts (${(extras.shifts || []).length})`}>
      {(extras.shifts || []).slice(0, 5).map(s => (
        <Row key={s.id} left={formatDateDDMMYYYY(s.start_at)} right={<StatusBadge status={s.status} />} />
      ))}
    </Section>
    <Section title={`Recent reports (${(extras.reports || []).length})`}>
      {(extras.reports || []).slice(0, 5).map(r => (
        <Row key={r.id} left={`${r.type} · ${formatDateDDMMYYYY(r.created_at)}`} right={r.resolved ? <StatusBadge status="RESOLVED" /> : <StatusBadge status={r.type} />} />
      ))}
    </Section>
  </>
);

const ClientProfileView = ({ data, extras }) => (
  <>
    <div className="flex items-center gap-4 mb-5">
      <div style={{ width: 64, height: 64, borderRadius: 12, background: "rgba(46,202,200,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Building2 size={28} color="#2ECAC8" />
      </div>
      <div>
        <div className="h-page" style={{ fontSize: 20 }}>{data.name}</div>
        <div className="text-muted mono" style={{ fontSize: 12 }}>{data.rc_number}</div>
      </div>
    </div>
    <Field icon={Mail} label="Contact email" value={data.contact_email} />
    <Field icon={Phone} label="Contact phone" value={data.contact_phone} />
    <Field icon={MapPin} label="Address" value={data.address || "—"} />
    <Section title="Financial">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Outstanding" value={formatNaira(data.invoices_outstanding || 0)} color={data.invoices_outstanding > 0 ? "#E63946" : "#2D9E5F"} />
        <Stat label="Lifetime paid" value={formatNaira(data.invoices_paid || 0)} color="#2D9E5F" />
      </div>
    </Section>
    <Section title={`Sites (${(extras.sites || []).length})`}>
      {(extras.sites || []).map(s => (
        <Row key={s.id} left={s.name} right={<span className="text-muted" style={{ fontSize: 12 }}>{s.lga}</span>} />
      ))}
    </Section>
    <Section title={`Contracts (${(extras.contracts || []).length})`}>
      {(extras.contracts || []).map(c => (
        <Row key={c.id} left={c.title} right={<StatusBadge status={c.status} />} />
      ))}
    </Section>
    <Section title={`Invoices (${(extras.invoices || []).length})`}>
      {(extras.invoices || []).slice(0, 6).map(i => (
        <Row key={i.id} left={`${i.invoice_no} · ${formatNaira(i.totals.total)}`} right={<StatusBadge status={i.status} />} />
      ))}
    </Section>
  </>
);

const UserProfileView = ({ data }) => (
  <>
    <div className="flex items-center gap-4 mb-5">
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(46,202,200,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
        <User size={28} color="#2ECAC8" />
      </div>
      <div>
        <div className="h-page" style={{ fontSize: 20 }}>{data.name}</div>
        <div className="text-muted" style={{ fontSize: 13 }}>{(data.role || "").replace("_", " ")}</div>
      </div>
    </div>
    <Field icon={Mail} label="Email" value={data.email} mono />
    <Field icon={Phone} label="Phone" value={data.phone} />
    <Field icon={Shield} label="Created" value={formatDateTimeWAT(data.created_at)} />
  </>
);

const Field = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3 py-2" style={{ borderBottom: "1px solid var(--border-dark)" }}>
    <Icon size={14} color="#8CA0B8" style={{ marginTop: 4 }} />
    <div className="flex-1">
      <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div className={`${mono ? "mono" : ""}`} style={{ fontSize: 14, color: "#fff", marginTop: 2 }}>{value || "—"}</div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mt-6">
    <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div className="p-3 rounded-lg" style={{ background: "rgba(46,202,200,0.05)", border: "1px solid var(--border-dark)" }}>
    <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
    <div style={{ fontSize: 18, color: color || "#fff", marginTop: 4 }}>{value}</div>
  </div>
);

const Row = ({ left, right }) => (
  <div className="flex justify-between items-center py-2" style={{ borderBottom: "1px solid var(--border-dark)", fontSize: 13 }}>
    <span style={{ color: "#fff" }}>{left}</span>
    {right}
  </div>
);
